import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AlertTriangle, ChevronDown } from "lucide-react";
import Button from "../../button-v1";
import Tooltip from "../../shared-modules/Tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../ui/select";
import { getCarePlan } from "../../../services/patient/carePlan";
import styles from "./styles.module.css";

// Wizard steps 2-5 and their fields, each selectable on its own (SCRUM-872).
// Field keys are formData paths. Careplan Notes (step 6) is intentionally
// left out; Chronic Conditions (step 1) is selected per condition instead.
export const COPY_FORWARD_SECTIONS = [
  {
    key: "medication",
    label: "Medication reconciliation",
    fields: [
      { key: "medications", label: "Medicines" },
      { key: "allergies", label: "Allergies" },
      { key: "reported_side_effect", label: "Side Effects" },
    ],
  },
  {
    key: "health_summary",
    label: "Health summary",
    fields: [
      { key: "physiological_data.weight", label: "Weight" },
      { key: "physiological_data.blood_glucose", label: "Blood Glucose" },
      { key: "physiological_data.bmi", label: "BMI" },
      { key: "physiological_data.blood_pressure", label: "Blood Pressure" },
      { key: "physiological_data.height", label: "Height" },
      { key: "behavioral_data.sleep", label: "Sleep" },
      { key: "behavioral_data.exercise", label: "Exercise" },
      { key: "behavioral_data.water_diet", label: "Diet / Water" },
    ],
  },
  {
    key: "problem_list",
    label: "Problem list & education",
    fields: [
      { key: "pmhx", label: "PMHx" },
      { key: "potential_barriers", label: "Potential Barriers" },
      { key: "recent_hospitalization", label: "Recent Hospitalization" },
      { key: "recommendations", label: "Recommendations" },
    ],
  },
  {
    key: "functional_assessment",
    label: "Functional assessment",
    fields: [
      { key: "activities_of_daily_living", label: "ADLs" },
      { key: "caregiver_assessment", label: "Caregiver Assessment" },
    ],
  },
];

const allFieldsChecked = () =>
  COPY_FORWARD_SECTIONS.flatMap(({ fields }) => fields).reduce(
    (acc, { key }) => ({ ...acc, [key]: true }),
    {}
  );

const monthKey = ({ month, year }) => `${month}-${year}`;

// Reads one copyable field (a formData path) from either the current
// month's form data or a raw care plan response - the two shapes differ
// only in medications ([{ name }] vs { name }) and list fields (multiline
// string vs array).
const readFieldValue = (data, path) => {
  if (!data) return "";
  if (path === "medications") {
    const meds = data.medications;
    return Array.isArray(meds) ? meds[0]?.name : meds?.name;
  }
  const [group, key] = path.split(".");
  return key ? data[group]?.[key] : data[group];
};

// One comparable string for a field value in either shape: list items or
// lines, trimmed, blank ones dropped. "" means the field is empty.
const normalizeValue = (value) =>
  (Array.isArray(value) ? value.join("\n") : `${value ?? ""}`)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n");

const CONDITION_DETAIL_FIELDS = ["name", "prognosis", "symptoms", "treatment_goals", "planned_interventions"];

// Internal per-item status (not shown on the items): "Has data" when the
// current month has different data that copying would replace, "Already
// added" when it already has the same data (nothing changes), none when the
// current month is empty. Only "Has data" triggers the warning/confirmation.
const HAS_DATA_TAG = "Has data";

// Hover message for every warning icon (fields, conditions, sections).
const OVERWRITE_WARNING =
  "Some selected sections already contain data for the current month. Copying forward will replace it.";

// Warning tooltips read better on a single line than wrapped at 420px.
const ONE_LINE_TOOLTIP = { whiteSpace: "nowrap", maxWidth: "none" };
const ALREADY_ADDED_TAG = "Already added";

const compareTag = (currentValue, sourceValue) => {
  if (!currentValue) return null;
  return currentValue === sourceValue ? ALREADY_ADDED_TAG : HAS_DATA_TAG;
};

// Native checkbox that can also show the "some selected" (indeterminate) dash.
const SectionCheckbox = ({ checked, indeterminate = false, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.checkbox}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
    />
  );
};

// One expandable group: a select-all checkbox (shows a dash when only some
// items are checked), an "x of y" count, and the individual item checkboxes.
const SelectionGroup = ({
  label,
  countNoun,
  items,
  checked,
  onToggleItem,
  onToggleAll,
  expanded,
  onToggleExpand,
}) => {
  const checkedCount = items.filter((item) => checked[item.key]).length;
  // Flag the section title too, so a collapsed section still shows where
  // the fields that would be replaced are.
  const hasReplaceableItem = items.some((item) => item.tag === HAS_DATA_TAG);
  return (
    <div className={styles.sectionRow}>
      <div className={styles.groupHeader}>
        <SectionCheckbox
          label={label}
          checked={items.length > 0 && checkedCount === items.length}
          indeterminate={checkedCount > 0 && checkedCount < items.length}
          onChange={onToggleAll}
        />
        <div className={styles.groupTitle}>
          <span className={styles.sectionTitleRow}>
            <span className={styles.sectionLabel}>{label}</span>
            {hasReplaceableItem && (
              // The Tooltip takes its z-index from the nearest positioned
              // parent (the dialog box, z-index auto = 0), which would put it
              // behind the Dialog overlay (z-index 1500) - lift it above.
              <Tooltip
                content={OVERWRITE_WARNING}
                position="top"
                zIndexOffset={2000}
                tooltipStyle={ONE_LINE_TOOLTIP}
              >
                <span className={styles.itemWarning}>
                  <AlertTriangle size={16} />
                </span>
              </Tooltip>
            )}
          </span>
          <span className={styles.groupCount}>
            {checkedCount} of {items.length} {countNoun}
          </span>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            className={styles.expandButton}
            onClick={onToggleExpand}
            aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          >
            <ChevronDown size={20} style={{ transform: expanded ? "rotate(180deg)" : "none" }} />
          </button>
        )}
      </div>
      {expanded && items.length > 0 && (
        <div className={styles.itemList}>
          {items.map((item) => (
            <label key={item.key} className={styles.itemRow}>
              <SectionCheckbox
                label={item.label}
                checked={!!checked[item.key]}
                onChange={() => onToggleItem(item.key)}
              />
              <span>{item.label}</span>
              {item.pill && <span className={styles.icdPill}>{item.pill}</span>}
              {item.tag === HAS_DATA_TAG && (
                // Icon only - the message is on the section heading's icon.
                <span className={styles.itemWarning} aria-label={OVERWRITE_WARNING}>
                  <AlertTriangle size={15} />
                </span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Copy Forward (SCRUM-871 / SCRUM-872): step 1 picks the source month, step 2
 * picks which chronic conditions and wizard fields to copy into the current
 * month. The copy itself is applied by the parent (onCopy) into the current
 * month's form; nothing is saved here.
 *
 * monthOptions: [{ month, year, label }] - months that can be copied from.
 * currentData: the current month's care plan (form data or API data), used
 * to flag items that already have data there and would be replaced.
 * onCopy({ sourceLabel, sourceData, fields, conditionIndexes }) - `fields`
 * are the checked formData paths, `conditionIndexes` the checked indexes into
 * sourceData.chronic_conditions.
 */
const CopyForwardModal = ({ open, setOpen, monthOptions = [], currentData, onCopy }) => {
  const [step, setStep] = useState("month");
  const [sourceKey, setSourceKey] = useState("");
  const [sourceData, setSourceData] = useState(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [checkedConditions, setCheckedConditions] = useState([]);
  const [fields, setFields] = useState(allFieldsChecked);
  // Chronic Conditions starts expanded, the other sections collapsed.
  const [expanded, setExpanded] = useState({ conditions: true });

  // Every open starts fresh on the month step, preselecting the latest month.
  useEffect(() => {
    if (!open) return;
    setStep("month");
    setSourceKey(monthOptions[0] ? monthKey(monthOptions[0]) : "");
    setSourceData(null);
    setCheckedConditions([]);
    setFields(allFieldsChecked());
    setExpanded({ conditions: true });
    // monthOptions is intentionally not a dependency: only reset on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sourceOption = monthOptions.find((option) => monthKey(option) === sourceKey);
  const conditions = sourceData?.chronic_conditions || [];
  // Same rule as the copy itself: a condition with the same ICD code as one
  // already in the current month replaces it - tagged only when that one
  // exists, and "Already added" when its details are identical.
  const conditionTag = (condition) => {
    const match = (currentData?.chronic_conditions || []).find(
      (current) => condition.icd_code && current.icd_code === condition.icd_code
    );
    if (!match) return null;
    const details = (cond) =>
      CONDITION_DETAIL_FIELDS.map((key) => normalizeValue(cond[key])).join("\u0000");
    return compareTag(details(match), details(condition));
  };
  const conditionItems = conditions.map((condition, index) => ({
    key: index,
    label: condition.name,
    pill: condition.icd_code,
    tag: conditionTag(condition),
  }));
  const sections = COPY_FORWARD_SECTIONS.map((section) => ({
    ...section,
    fields: section.fields.map((field) => ({
      ...field,
      tag: compareTag(
        normalizeValue(readFieldValue(currentData, field.key)),
        normalizeValue(readFieldValue(sourceData, field.key))
      ),
    })),
  }));
  // Checked items whose current-month data would actually change ("Has
  // data" only - "Already added" loses nothing), for the confirmation step.
  const replacedItems = [
    ...conditionItems
      .filter((item) => item.tag === HAS_DATA_TAG && checkedConditions[item.key])
      .map((item) => ({ group: "Chronic Conditions", label: `${item.label} (${item.pill})` })),
    ...sections.flatMap((section) =>
      section.fields
        .filter((field) => field.tag === HAS_DATA_TAG && fields[field.key])
        .map((field) => ({ group: section.label, label: field.label }))
    ),
  ];
  const hasSelection =
    checkedConditions.some(Boolean) || Object.values(fields).some(Boolean);

  const loadSource = async (option) => {
    setLoadingSource(true);
    try {
      const response = await getCarePlan(option.month, option.year);
      if (!response?.data?.id) {
        toast.error(`No care plan found for ${option.label}`);
        return false;
      }
      setSourceData(response.data);
      setCheckedConditions((response.data.chronic_conditions || []).map(() => true));
      return true;
    } catch (error) {
      toast.error(`Failed to load the ${option.label} care plan`);
      return false;
    } finally {
      setLoadingSource(false);
    }
  };

  // Step 1 -> step 2
  const handleContinue = async () => {
    if (!sourceOption) return;
    if (await loadSource(sourceOption)) setStep("sections");
  };

  // Changing the month on step 2 reloads that month's conditions.
  const handleSourceChange = async (value) => {
    setSourceKey(value);
    if (step !== "sections") return;
    const option = monthOptions.find((o) => monthKey(o) === value);
    if (option && !(await loadSource(option))) {
      setSourceData(null);
      setCheckedConditions([]);
    }
  };

  const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleCondition = (index) => {
    setCheckedConditions((prev) => prev.map((value, i) => (i === index ? !value : value)));
  };

  const setSectionFields = (section, value) => {
    setFields((prev) => ({
      ...prev,
      ...section.fields.reduce((acc, { key }) => ({ ...acc, [key]: value }), {}),
    }));
  };

  const handleCopyClick = () => {
    if (replacedItems.length > 0) setStep("confirm");
    else handleCopy();
  };

  const handleCopy = () => {
    onCopy({
      sourceLabel: sourceOption?.label,
      sourceData,
      fields: Object.keys(fields).filter((key) => fields[key]),
      conditionIndexes: checkedConditions
        .map((checked, index) => (checked ? index : null))
        .filter((index) => index !== null),
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent className={styles.dialog}>
        <DialogHeader>
          <DialogTitle className={styles.title}>Copy Forward to Current Month</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {step === "confirm" ? (
            <>
              <p className={styles.confirmTitle}>Replace existing data?</p>
              <p className={styles.intro}>
                {replacedItems.length === 1 ? "This item already has" : `These ${replacedItems.length} items already have`}{" "}
                data in the current month. It will be replaced with the{" "}
                <strong>{sourceOption?.label}</strong> data:
              </p>
              <ul className={styles.replacedList}>
                {replacedItems.map((item) => (
                  <li key={`${item.group}-${item.label}`}>
                    <span className={styles.replacedGroup}>{item.group}:</span> {item.label}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
          <p className={styles.intro}>
            Select the month from which you want to copy forward the Careplan to{" "}
            <strong>Current Month</strong>
          </p>

          <label className={styles.fieldLabel}>Copy from</label>
          {monthOptions.length > 0 ? (
            <Select value={sourceKey} onChange={handleSourceChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select month">{sourceOption?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((option) => (
                  <SelectItem key={monthKey(option)} value={monthKey(option)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className={styles.empty}>No care plans from the last 12 months to copy from.</p>
          )}


          {step === "sections" && sourceData && (
            <div className={styles.sectionList}>
              <SelectionGroup
                label="Chronic Conditions"
                countNoun="conditions"
                items={conditionItems}
                checked={checkedConditions}
                onToggleItem={toggleCondition}
                onToggleAll={(value) => setCheckedConditions(conditions.map(() => value))}
                expanded={!!expanded.conditions}
                onToggleExpand={() => toggleExpanded("conditions")}
              />

              {sections.map((section) => (
                <SelectionGroup
                  key={section.key}
                  label={section.label}
                  countNoun="fields"
                  items={section.fields}
                  checked={fields}
                  onToggleItem={(key) => setFields((prev) => ({ ...prev, [key]: !prev[key] }))}
                  onToggleAll={(value) => setSectionFields(section, value)}
                  expanded={!!expanded[section.key]}
                  onToggleExpand={() => toggleExpanded(section.key)}
                />
              ))}
            </div>
          )}
            </>
          )}
        </DialogBody>
        <DialogFooter className={styles.footer}>
          <Button
            variant="ghost"
            className={styles.footerButton}
            onClick={() => (step === "confirm" ? setStep("sections") : setOpen(false))}
          >
            {step === "confirm" ? "Go back" : "Cancel"}
          </Button>
          <Button
            variant="primary"
            className={`${styles.footerButton} ${styles.primaryButton}`}
            loading={loadingSource}
            disabled={
              loadingSource ||
              !sourceOption ||
              (step === "sections" && (!sourceData || !hasSelection))
            }
            onClick={
              step === "month" ? handleContinue : step === "sections" ? handleCopyClick : handleCopy
            }
          >
            {step === "confirm" ? "Yes, replace and copy" : "Copy to Current month"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyForwardModal;
